import { useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { applyPalette, saveAppearance } from '@/lib/appearance';
import { applicaV3, paletteDaApplicare, TETTO_STRATO_MS, useV3 } from '@/lib/v3';
import { resetAppearance, setCustomColors, setPreset } from '@/store/appearance.slice';

/**
 * Le preferenze d'aspetto e i modi per cambiarle, SENZA effetti: chi le legge (Impostazioni › Aspetto) non applica
 * niente al documento. L'applicazione e' di `useAppearance`, montato UNA volta in AppShell (flotta su #757, R2: due
 * montaggi applicavano due volte, e nel caso del rifiuto la palette v3 lampeggiava).
 */
export function useAppearancePrefs() {
  const prefs = useAppSelector((s) => s.appearance);
  const dispatch = useAppDispatch();
  return {
    prefs,
    presetId: prefs.presetId,
    setPreset: (id: string) => dispatch(setPreset(id)),
    setCustomColors: (colors: { bg?: string; accent?: string }) =>
      dispatch(setCustomColors(colors)),
    reset: () => dispatch(resetAppearance()),
  };
}

/**
 * Subscribes to the appearance prefs, applies the resolved palette to the
 * document (CSS vars + native Telegram chrome) and persists prefs whenever they
 * change. Mount ONCE (in AppShell) — calling it elsewhere would just redo the
 * same idempotent apply (e, con la v3, ridurrebbe la palette a un lampo nel caso del rifiuto dello strato).
 *
 * Initial prefs are hydrated synchronously in the slice's initialState
 * (`loadStoredAppearance`), and `main.tsx` does a one-shot apply before React
 * mounts, so there's no flash of the default theme.
 */
export function useAppearance() {
  const risultato = useAppearancePrefs();
  const { prefs } = risultato;

  // La v3 (se `useV3()`) vale al posto del tema, ma il `presetId` salvato non si tocca: spenta, `paletteDaApplicare`
  // torna a risolvere quello.
  const v3 = useV3();
  // Se lo strato (fogli e caratteri) non arriva, la palette della v3 NON si applica: senza lo strato sarebbero colori nuovi su una forma e dei
  // caratteri vecchi (flotta su #753, nota 3). Vale il tema salvato, e si riprova al prossimo cambio del predicato.
  const [stratoNonArrivato, setStratoNonArrivato] = useState(false);
  const palette = useMemo(() => paletteDaApplicare(prefs, v3 && !stratoNonArrivato), [prefs, v3, stratoNonArrivato]);

  useEffect(() => {
    applyPalette(palette);
    saveAppearance(prefs);
  }, [palette, prefs]);

  // `data-v3` sulla radice (forma, caratteri, sfondo): scritto da `applicaV3` e da nessun altro. No-op senza il flag.
  // ⏱ Un tetto, lo stesso del boot (`TETTO_STRATO_MS`): un import che si BLOCCA non rifiuta e non risolve, e senza tetto la palette v3 resterebbe
  // applicata SENZA `data-v3` (flotta su #757, R1). Scaduto il tetto vale il tema salvato; se lo strato arriva dopo, `applicaV3` scrive `data-v3` e
  // la risposta rimette la palette v3: i due insieme.
  useEffect(() => {
    let vivo = true;
    const tetto = v3
      ? setTimeout(() => {
          if (vivo) setStratoNonArrivato(true);
        }, TETTO_STRATO_MS)
      : undefined;
    void applicaV3(v3).then((applicata) => {
      clearTimeout(tetto);
      if (vivo) setStratoNonArrivato(v3 && !applicata);
    });
    return () => {
      vivo = false;
      clearTimeout(tetto);
    };
  }, [v3]);

  return risultato;
}
